/**
 * Pelvis re-own — ADR-029's one door: transfer spine-donor skin weight that
 * lies BELOW the resolved station to the bone the thighs hang from, so a
 * bow flexes the spine above the pelvis instead of swinging the buttock
 * forward about the hips.
 *
 * Selection is on BIND positions via the mesh's stored bindMatrix — the
 * pose-independent frame (the elbow-evidence law: a playing clip must not
 * move the measurement). Below the station the transfer is total; across
 * the seam above it the factor ramps 1→0 linearly, so the lumbar keeps its
 * paint and the boundary has no step.
 *
 * Weight LAWS, same as the crease band and the wrist re-own: only DONOR
 * slots are taken from — flesh weighted elsewhere (thighs, arms, a hand
 * hanging at hip height) is never touched; partition of unity is preserved
 * by construction (weight moves between slots, never appears or vanishes)
 * and MEASURED anyway (worstWeightSumError); the four-slot ceiling evicts
 * the weakest, counted and reported, never silent.
 *
 * Depends on: three, kernel (ResolvedPelvisOwnership). Depended on by: the
 * Document driver's torso pass, strictly BEFORE the hinge band (so a hip
 * hinge's parent holds the flesh its band reads).
 */
import { Matrix4, Vector3, type SkinnedMesh } from 'three';
import type { ResolvedPelvisOwnership } from '@harryhausen/kernel';
import { seatInfluences } from '../weights/seat-influences.js';

export interface PelvisReownReport {
  readonly owner: string;
  /**
   * Set when the mesh was SKIPPED: its skeleton holds neither the owner nor
   * any donor — a skin of another skeleton in a multi-skeleton target, not
   * this rig's flesh (Codex P2, #108). Zero counts, never silent.
   */
  readonly skipped?: string;
  readonly verticesReowned: number;
  /** Full four-slot vertices near the seam's top left AS PAINTED because the owner's share would not seat — counted, never silent. */
  readonly verticesUnseated: number;
  /** Total weight mass moved onto the owner — the disease's size, in influence units. */
  readonly weightMoved: number;
  readonly influencesEvicted: number;
  readonly largestEviction: number;
  readonly worstWeightSumError: number;
}

export class PelvisReownError extends Error {
  constructor(readonly issues: readonly string[]) {
    super(`Cannot re-own pelvis flesh (${issues.length} issue(s)):\n` + issues.map((i) => `  - ${i}`).join('\n'));
  }
}

/**
 * Below this the owner is already at its declared share and the vertex is left
 * untouched — the door must be a BIT-for-bit no-op on flesh it has already
 * re-owned, or a re-imported export drifts (#110's law). Float32 weights
 * round-trip to a few 1e-8; this is two orders above that and invisible.
 */
const REOWN_SETTLED = 1e-6;

export function reownPelvisFlesh(mesh: SkinnedMesh, resolved: ResolvedPelvisOwnership): PelvisReownReport {
  const issues: string[] = [];
  const skinIndex = mesh.geometry.getAttribute('skinIndex');
  const skinWeight = mesh.geometry.getAttribute('skinWeight');
  const position = mesh.geometry.getAttribute('position');
  if (!skinIndex || !skinWeight || !position) {
    throw new PelvisReownError(['mesh is not skinned (no skinIndex/skinWeight/position)']);
  }
  if (!(resolved.seamWorld > 0)) {
    throw new PelvisReownError([`seam must be positive, got ${resolved.seamWorld}`]);
  }
  const slotOf = new Map(mesh.skeleton.bones.map((b, i) => [b.name, i] as const));
  // Donors absent from THIS skeleton are fine — a garment mesh may share the
  // skeleton but the declaration names the family's full chain.
  const donorSlots = new Set<number>();
  for (const d of resolved.donors) {
    const s = slotOf.get(d);
    if (s !== undefined) donorSlots.add(s);
  }
  const ownerSlot = slotOf.get(resolved.owner);
  if (ownerSlot === undefined) {
    if (donorSlots.size === 0) {
      // Neither the owner nor a donor: a skin of ANOTHER skeleton (a
      // multi-skeleton target whose body rig was selected) — not this rig's
      // flesh. Skipped and said, never thrown (Codex P2, #108).
      return {
        owner: resolved.owner,
        skipped: `skeleton "${mesh.skeleton.bones[0]?.name ?? '?'}…" holds neither "${resolved.owner}" nor any donor`,
        verticesReowned: 0,
        verticesUnseated: 0,
        weightMoved: 0,
        influencesEvicted: 0,
        largestEviction: 0,
        worstWeightSumError: 0,
      };
    }
    // Donors present, owner absent: the rig and the skeleton disagree — a contradiction, not a foreign skin.
    throw new PelvisReownError([`owner "${resolved.owner}" is not in the skeleton, yet its donors are`]);
  }

  const station = new Vector3(resolved.stationWorld.x, resolved.stationWorld.y, resolved.stationWorld.z);
  const axis = new Vector3(resolved.axisWorld.x, resolved.axisWorld.y, resolved.axisWorld.z).normalize();
  const bind = new Matrix4().copy(mesh.bindMatrix);
  const influencesPerVertex = skinIndex.itemSize;
  const v = new Vector3();
  let verticesReowned = 0;
  let verticesUnseated = 0;
  let weightMoved = 0;
  let influencesEvicted = 0;
  let largestEviction = 0;
  let worstWeightSumError = 0;

  for (let i = 0; i < position.count && donorSlots.size > 0; i += 1) {
    v.fromBufferAttribute(position, i).applyMatrix4(bind).sub(station);
    const rise = v.dot(axis);
    if (rise >= resolved.seamWorld) continue; // lumbar and above — the spine keeps its paint
    const f = rise <= 0 ? 1 : 1 - rise / resolved.seamWorld;

    // The ramp declares a DESTINATION, not a transfer: at this height the
    // owner holds fraction f of the spine-painted flesh — donors plus whatever
    // the owner already holds — and the donors share the rest in proportion.
    // Stated as a transfer it compounds, because a second run moves f of what
    // the FIRST run left: measured 1.0 → .5/.5 → .75/.25 across the seam,
    // which broke the export → re-import bit-identity law (#110) and, on the
    // kobold, grew the owner enough to evict a thigh twist. Below the station
    // f = 1 and the two readings agree, which is why it hid there.
    // (The owner's own paint joins the pool by the same declaration — the
    // pelvis owns its flesh. Measured: Tripo paints Pelvis/Hip zero, so on the
    // declared family the pool is the donors'.)
    let donorTotal = 0;
    let ownerExisting = 0;
    for (let k = 0; k < influencesPerVertex; k += 1) {
      const w = skinWeight.getComponent(i, k);
      if (w <= 0) continue;
      const slot = skinIndex.getComponent(i, k);
      if (donorSlots.has(slot)) donorTotal += w;
      else if (slot === ownerSlot) ownerExisting += w;
    }
    if (donorTotal <= 0) continue; // not spine-painted flesh — never touched
    const pool = donorTotal + ownerExisting;
    const ownerShare = f * pool;
    const moved = ownerShare - ownerExisting;
    // Already at its destination (a re-imported export): a no-op, and it must
    // stay a BIT-for-bit no-op, so the vertex is never rewritten.
    //
    // The test is DELIBERATELY ONE-SIDED — this door only ever moves flesh
    // TOWARD the owner, never away from it. Codex (P2 on #108) read the
    // negative branch as a missed case: an owner already above the ramp should
    // be rebalanced down, donors taking the excess. Built and measured on the
    // kobold, that breaks #110's export → re-import law, and the measurement
    // says why. This door runs BEFORE the hinge band, and the band moves donor
    // weight into the grafted twist helpers and renormalizes — which RAISES
    // the owner's share of what remains. On kobold vertex 232 (f = 0.687488,
    // bit-stable across the round trip) this door writes 0.388229 = f × pool,
    // and the exported vertex holds 0.393534: the band's doing, downstream and
    // legitimate. Dragging it back to the ramp on re-import does not undo a
    // compounded transfer — it undoes the HINGE BAND, on 166 vertex slots.
    //
    // So an owner above the ramp is never this door's to correct: it cannot be
    // told apart from a later door's contribution or an artist's own paint,
    // and the compounding Codex worried about is gone at the source — the
    // destination form cannot grow the owner on a second pass.
    if (moved <= REOWN_SETTLED) continue;
    const donorScale = (pool - ownerShare) / donorTotal;
    const slots = new Map<number, number>();
    for (let k = 0; k < influencesPerVertex; k += 1) {
      const w = skinWeight.getComponent(i, k);
      if (w <= 0) continue;
      const slot = skinIndex.getComponent(i, k);
      if (slot === ownerSlot) continue; // folded into ownerShare below
      const keep = donorSlots.has(slot) ? w * donorScale : w;
      if (keep > 0) slots.set(slot, (slots.get(slot) ?? 0) + keep);
    }
    slots.set(ownerSlot, ownerShare);

    // Four-slot ceiling: DON'T MOVE WHAT YOU CANNOT SEAT — the one law of the
    // weight doors (seat-influences, #109). At the top of the seam the pelvis
    // is the smallest of five influences on a full four-slot vertex; the
    // vertex stays AS PAINTED — nothing moved, nothing renormalized — until
    // the owner's share STRICTLY outranks the weakest existing influence
    // (a tie is unseated: slot order never decides, Codex P2 #108). Counted.
    const { kept: top, evicted, unseatedReceivers } = seatInfluences(slots, new Set([ownerSlot]), influencesPerVertex);
    if (unseatedReceivers.length > 0) {
      verticesUnseated += 1;
      continue;
    }
    influencesEvicted += evicted.length;
    for (const [, w] of evicted) largestEviction = Math.max(largestEviction, w);
    const total = top.reduce((s, [, w]) => s + w, 0);
    if (!(total > 0)) {
      issues.push(`vertex ${i}: re-own left zero total weight — refusing to emit an unskinned vertex`);
      continue;
    }
    for (let k = 0; k < influencesPerVertex; k += 1) {
      const entry = top[k];
      skinIndex.setComponent(i, k, entry === undefined ? 0 : entry[0]);
      skinWeight.setComponent(i, k, entry === undefined ? 0 : entry[1] / total);
    }
    let sum = 0;
    for (let k = 0; k < influencesPerVertex; k += 1) sum += skinWeight.getComponent(i, k);
    worstWeightSumError = Math.max(worstWeightSumError, Math.abs(sum - 1));
    verticesReowned += 1;
    weightMoved += moved;
  }
  if (issues.length > 0) throw new PelvisReownError(issues);
  skinIndex.needsUpdate = true;
  skinWeight.needsUpdate = true;

  return { owner: resolved.owner, verticesReowned, verticesUnseated, weightMoved, influencesEvicted, largestEviction, worstWeightSumError };
}
